// src/hooks/useShortability.js
//
// Batched shortability lookup for symbol pickers / autocompletes.
//
// Usage:
//   const { lookup, get } = useShortability();
//   lookup(['AAPL', 'TSLA', 'NVDA']);      // schedules a batch fetch
//   const status = get('AAPL');             // 'easy_to_borrow' | 'hard_to_borrow' | 'not_shortable' | undefined
//
// Strategy:
//   - Caller calls lookup(symbols) on every render with the symbols
//     currently visible in the picker (or as user types into autocomplete).
//   - Hook batches into a single REST call after 300ms debounce.
//   - Cached results are reused; only NEW symbols hit the network.
//   - 5-minute TTL per symbol (shortability changes slowly enough; if a
//     stock becomes hard-to-borrow during the day, 5min lag is fine).

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASEURL;
const DEBOUNCE_MS = 300;
const TTL_MS = 5 * 60 * 1000;
const MAX_BATCH = 50;   // backend caps at 100, leave headroom


export function useShortability({ enabled = true } = {}) {
  // symbol → { status, fetchedAt }
  const cacheRef = useRef(new Map());
  const pendingSetRef = useRef(new Set());
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);

  // Force re-render when cache updates
  const [version, setVersion] = useState(0);
  const bump = () => setVersion(v => v + 1);

  const flush = useCallback(async () => {
    if (!enabled) return;
    if (inFlightRef.current) return;
    if (pendingSetRef.current.size === 0) return;

    // Pull the next batch out of the pending set
    const batch = Array.from(pendingSetRef.current).slice(0, MAX_BATCH);
    batch.forEach(s => pendingSetRef.current.delete(s));

    inFlightRef.current = true;
    try {
      const resp = await axios.get(`${API_BASE}/api/account/alpaca/shortability`, {
        params: { symbols: batch.join(',') },
        headers: { Authorization: localStorage.getItem('token') },
        timeout: 8000,
      });

      const data = resp.data || {};
      const results = data.results || {};
      const now = Date.now();

      // Update cache for every symbol in the batch — even ones not in the
      // response (treat missing as 'not_shortable' so we don't refetch).
      for (const sym of batch) {
        const status = results[sym] || results[sym.toUpperCase()] || 'not_shortable';
        cacheRef.current.set(sym.toUpperCase(), { status, fetchedAt: now });
      }
      bump();
    } catch (err) {
      // On error, mark each as fetched-recently with status 'loading'
      // so we don't hammer the endpoint, but don't pollute with bad data.
      // Next flush attempt (after TTL) will retry.
      const now = Date.now();
      for (const sym of batch) {
        if (!cacheRef.current.has(sym.toUpperCase())) {
          cacheRef.current.set(sym.toUpperCase(), { status: 'loading', fetchedAt: now });
        }
      }
      bump();
    } finally {
      inFlightRef.current = false;

      // If more symbols accumulated while in-flight, schedule another flush
      if (pendingSetRef.current.size > 0) {
        timerRef.current = setTimeout(flush, DEBOUNCE_MS);
      }
    }
  }, [enabled]);

  const lookup = useCallback((symbols) => {
    if (!enabled || !Array.isArray(symbols) || symbols.length === 0) return;

    const now = Date.now();
    let queued = 0;
    for (const raw of symbols) {
      if (!raw) continue;
      const sym = String(raw).toUpperCase();
      const cached = cacheRef.current.get(sym);
      if (cached && cached.status !== 'loading' && now - cached.fetchedAt < TTL_MS) {
        continue;
      }
      pendingSetRef.current.add(sym);
      queued++;
    }

    if (queued > 0) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, DEBOUNCE_MS);
    }
  }, [enabled, flush]);

  const get = useCallback((symbol) => {
    if (!symbol) return undefined;
    const cached = cacheRef.current.get(String(symbol).toUpperCase());
    return cached?.status;
  }, [version]); // version dep forces re-evaluation after cache update

  // Cleanup pending timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { lookup, get };
}
